// Phase 9.6.2 — Shared property-based item listing.
//
// Renders a page with:
//   • Top-level search box + status filter + applicability filter.
//   • Property count header ("N active properties · M items total").
//   • One collapsible card per active property, sorted by health.
//     Each card shows its own item table with sortable columns and
//     the same applicability toggle as the Location Detail page.
//
// Used by both the Bar Stock tab (`item_scope="bar"`) and the new
// Merchandise tab (`item_scope="merchandise"`) so both stay
// consistent.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, detailFromError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { STATUS_META } from "./statusMeta";

// Column config — used both for header labels AND sortable clicks.
const COLUMNS = [
  { key: "sku", label: "SKU", sortable: true, align: "left", w: "w-24" },
  { key: "name", label: "Item Name", sortable: true, align: "left" },
  { key: "category", label: "Category", sortable: true, align: "left" },
  { key: "sub_category", label: "Sub Category", sortable: true, align: "left" },
  { key: "uom", label: "UOM", sortable: true, align: "left", w: "w-16" },
  { key: "qty_available", label: "Current Stock", sortable: true, align: "right", w: "w-28" },
  { key: "reorder_level", label: "Reorder", sortable: true, align: "right", w: "w-24" },
  { key: "status", label: "Status", sortable: true, align: "left", w: "w-32" },
  { key: "is_applicable", label: "Applicable", sortable: false, align: "left", w: "w-32" },
];

const STATUS_ROW_BG = {
  out_of_stock: "bg-rose-50/60",
  danger: "bg-rose-50/40",
  reorder: "bg-amber-50/50",
  ok: "bg-emerald-50/30",
  overstocked: "bg-emerald-50/20",
  slow_moving: "bg-indigo-50/40",
  inactive_here: "bg-slate-50/60 opacity-60",
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || {
    label: status || "—",
    className: "bg-slate-500/10 text-slate-800 border-slate-500/30",
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${meta.className}`}>
      {meta.label}
    </span>
  );
}

function num(v, decimals = 0) {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  return Number(v).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
}

function PropertyItemsTable({ items, onToggleApplicability, savingSku, sortState, onSort }) {
  if (!items || items.length === 0) {
    return (
      <div className="text-xs text-muted-foreground italic py-4 text-center border border-dashed border-border/50 rounded-md">
        No items configured for this outlet.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-md border border-border/60">
      <table className="w-full text-[12px]" data-testid="property-items-table">
        <thead className="bg-muted/50 sticky top-0 z-[1]">
          <tr>
            <th className="text-[10px] text-muted-foreground font-medium px-2 py-2 w-8">#</th>
            {COLUMNS.map((col) => (
              <th
                key={col.key}
                className={`text-[10px] text-muted-foreground font-medium px-2 py-2 ${
                  col.align === "right" ? "text-right" : "text-left"
                } ${col.w || ""} ${col.sortable ? "cursor-pointer hover:text-foreground" : ""}`}
                onClick={() => col.sortable && onSort(col.key)}
                data-testid={`property-col-${col.key}`}
              >
                {col.label}
                {sortState?.key === col.key && (
                  <span className="ml-1">{sortState.dir === "asc" ? "↑" : "↓"}</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((r, i) => {
            const rowCls = STATUS_ROW_BG[r.status] || "";
            return (
              <tr
                key={r.sku}
                className={`${rowCls} border-t border-border/40`}
                data-testid={`property-item-row-${r.sku}`}
              >
                <td className="px-2 py-1.5 text-[10px] text-muted-foreground">{i + 1}</td>
                <td className="px-2 py-1.5 font-mono text-[11px]">
                  <Link
                    to={`/tools/fnb-po/items/${encodeURIComponent(r.sku)}`}
                    className="hover:underline underline-offset-4"
                  >
                    {r.sku}
                  </Link>
                </td>
                <td className="px-2 py-1.5 font-medium max-w-[260px]">
                  <span className="truncate block" title={r.name}>{r.name}</span>
                </td>
                <td className="px-2 py-1.5 text-[11px] text-muted-foreground">{r.category || "—"}</td>
                <td className="px-2 py-1.5 text-[11px] text-muted-foreground">{r.sub_category || "—"}</td>
                <td className="px-2 py-1.5 text-[11px] text-muted-foreground">{r.uom || "—"}</td>
                <td className="px-2 py-1.5 text-right font-mono">{num(r.qty_available, 1)}</td>
                <td className="px-2 py-1.5 text-right font-mono">{num(r.reorder_level)}</td>
                <td className="px-2 py-1.5"><StatusPill status={r.status} /></td>
                <td className="px-2 py-1.5">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={!!r.is_applicable}
                      disabled={savingSku === r.sku}
                      onClick={() => onToggleApplicability(r.sku, !r.is_applicable)}
                      title={r.is_applicable ? "Mark not applicable at this outlet" : "Mark applicable"}
                      className={`h-4 w-7 rounded-full transition-colors relative disabled:opacity-50 ${
                        r.is_applicable ? "bg-emerald-600" : "bg-slate-300"
                      }`}
                      data-testid={`property-toggle-${r.sku}`}
                    >
                      <span
                        className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all ${
                          r.is_applicable ? "left-3.5" : "left-0.5"
                        }`}
                      />
                    </button>
                    <Badge
                      variant="outline"
                      className={`text-[9px] py-0 px-1.5 ${
                        r.applicability_source === "manual"
                          ? "border-emerald-500/50 text-emerald-800"
                          : "border-slate-300 text-slate-500"
                      }`}
                    >
                      {r.applicability_source === "manual" ? "Manual" : "Auto"}
                    </Badge>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PropertyCard({
  tile, itemScope, search, statusFilter, applicabilityFilter,
  expanded, onExpandChange,
}) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingSku, setSavingSku] = useState(null);
  const [sortState, setSortState] = useState({ key: "category", dir: "asc" });

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("item_scope", itemScope);
      params.set("filter", applicabilityFilter);
      params.set("include_inactive_units", "true");
      const res = await api.get(
        `/fnb/locations/${encodeURIComponent(tile.location_id)}/items?${params}`,
      );
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error(detailFromError(e) || "Could not load items");
    } finally {
      setLoading(false);
    }
  }, [tile.location_id, itemScope, applicabilityFilter]);

  useEffect(() => {
    if (expanded && items === null) loadItems();
  }, [expanded, items, loadItems]);

  // Refetch when filter changes AND card is currently expanded.
  useEffect(() => {
    if (expanded) loadItems();
  }, [applicabilityFilter, loadItems, expanded]);

  const filteredSorted = useMemo(() => {
    let rows = items || [];
    if (search) {
      const s = search.toLowerCase();
      rows = rows.filter((r) =>
        (r.sku || "").toLowerCase().includes(s) ||
        (r.name || "").toLowerCase().includes(s),
      );
    }
    if (statusFilter) rows = rows.filter((r) => r.status === statusFilter);
    // Sort
    rows = [...rows].sort((a, b) => {
      const av = a[sortState.key];
      const bv = b[sortState.key];
      const cmp =
        av === null || av === undefined ? 1 :
        bv === null || bv === undefined ? -1 :
        typeof av === "number" ? av - bv :
        String(av).localeCompare(String(bv));
      return sortState.dir === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [items, search, statusFilter, sortState]);

  const onSort = useCallback((key) => {
    setSortState((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" },
    );
  }, []);

  const toggleApplicability = useCallback(async (sku, nextValue) => {
    setSavingSku(sku);
    let snapshot = null;
    setItems((prev) => {
      snapshot = prev;
      if (!prev) return prev;
      return prev.map((r) =>
        r.sku === sku
          ? { ...r, is_applicable: !!nextValue, applicability_source: "manual" }
          : r,
      );
    });
    try {
      await api.patch(
        `/fnb/locations/${encodeURIComponent(tile.location_id)}/items/${encodeURIComponent(sku)}/applicability`,
        { is_applicable: !!nextValue },
      );
      toast.success(
        nextValue
          ? "Marked applicable. Persistent — future uploads won't reset."
          : "Marked not applicable. Persistent — future uploads won't reset.",
      );
    } catch (e) {
      if (snapshot !== null) setItems(snapshot);
      toast.error(detailFromError(e) || "Could not save. Try again.");
    } finally {
      setSavingSku(null);
    }
  }, [tile.location_id]);

  return (
    <Card
      className="border border-border/70"
      data-testid={`property-card-${tile.location_id}`}
    >
      <button
        type="button"
        className="w-full text-left px-4 py-3 sticky top-0 bg-background/95 backdrop-blur z-[2] border-b border-border/40 hover:bg-muted/30 transition-colors"
        onClick={() => onExpandChange(!expanded)}
        data-testid={`property-toggle-expand-${tile.location_id}`}
      >
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground w-4">
            {expanded ? "▾" : "▸"}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-[15px] truncate" data-testid={`property-name-${tile.location_id}`}>
                {tile.display_name}
              </h3>
              {tile.is_active === false && (
                <span className="px-1.5 py-0.5 rounded bg-slate-200 text-[9px] uppercase tracking-wide text-slate-700">
                  Inactive unit
                </span>
              )}
              {tile.is_warehouse && (
                <span className="px-1.5 py-0.5 rounded bg-muted/60 text-[9px] uppercase tracking-wide text-muted-foreground">
                  Warehouse
                </span>
              )}
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              <b className="text-foreground">{tile.items_total || 0} items</b>
              <span className="mx-1.5">·</span>
              <span className="text-emerald-800">{tile.items_ok || 0} OK</span>
              <span className="mx-1.5">·</span>
              <span className="text-amber-800">{tile.items_reorder_or_danger || 0} reorder</span>
              <span className="mx-1.5">·</span>
              <span className="text-rose-800">{tile.items_out_of_stock || 0} OOS</span>
            </div>
          </div>
        </div>
      </button>
      {expanded && (
        <CardContent className="p-4">
          {loading && !items ? (
            <div className="text-xs text-muted-foreground italic py-4">Loading items…</div>
          ) : (
            <PropertyItemsTable
              items={filteredSorted}
              onToggleApplicability={toggleApplicability}
              savingSku={savingSku}
              sortState={sortState}
              onSort={onSort}
            />
          )}
        </CardContent>
      )}
    </Card>
  );
}

export default function LocationSegmentedListing({
  title = "Property listing",
  itemScope,          // "bar" | "merchandise"
  helperText = "",
  emptyText = "No properties available for this scope.",
}) {
  const [tiles, setTiles] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [applicabilityFilter, setApplicabilityFilter] = useState("applicable");
  const [expandedByLoc, setExpandedByLoc] = useState(() => new Set());

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(
        `/fnb/analytics/location-tiles?item_scope=${encodeURIComponent(itemScope)}`,
      );
      const rows = res.data?.tiles || [];
      setTiles(rows);
      // Expand the first 3 properties by default so the user sees data
      // immediately, without waiting for 30+ item-fetch round-trips.
      setExpandedByLoc(new Set(rows.slice(0, 3).map((t) => t.location_id)));
    } catch (e) {
      toast.error(detailFromError(e) || "Could not load properties");
    } finally {
      setLoading(false);
    }
  }, [itemScope]);

  useEffect(() => { reload(); }, [reload]);

  const totalItems = (tiles || []).reduce((s, t) => s + (t.items_total || 0), 0);
  const activeProps = (tiles || []).filter((t) => t.is_active !== false).length;

  const toggleExpanded = useCallback((locId, next) => {
    setExpandedByLoc((prev) => {
      const s = new Set(prev);
      if (next) s.add(locId); else s.delete(locId);
      return s;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpandedByLoc(new Set((tiles || []).map((t) => t.location_id)));
  }, [tiles]);

  const collapseAll = useCallback(() => setExpandedByLoc(new Set()), []);

  if (loading && !tiles) {
    return <div className="text-sm text-muted-foreground" data-testid="listing-loading">Loading…</div>;
  }
  if (!tiles || tiles.length === 0) {
    return <div className="text-sm text-muted-foreground italic" data-testid="listing-empty">{emptyText}</div>;
  }

  return (
    <div className="space-y-4" data-testid={`fnb-listing-${itemScope}`}>
      <div>
        <h1 className="text-2xl font-semibold tracking-tight" data-testid="listing-title">{title}</h1>
        {helperText && <p className="text-sm text-muted-foreground mt-1">{helperText}</p>}
        <div className="mt-1 text-xs text-muted-foreground" data-testid="listing-count-strip">
          <b className="text-foreground">{activeProps} active properties</b> · <b className="text-foreground">{totalItems.toLocaleString()}</b> items in scope
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search SKU or name across all properties…"
          className="w-[300px] text-xs"
          data-testid="listing-search"
        />
        <Select value={statusFilter || "__all"} onValueChange={(v) => setStatusFilter(v === "__all" ? "" : v)}>
          <SelectTrigger className="w-[160px] text-xs" data-testid="listing-status-filter">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All statuses</SelectItem>
            {["out_of_stock", "danger", "reorder", "ok", "overstocked", "slow_moving", "inactive_here"].map((s) => (
              <SelectItem key={s} value={s}>{STATUS_META[s]?.label || s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={applicabilityFilter} onValueChange={setApplicabilityFilter}>
          <SelectTrigger className="w-[180px] text-xs" data-testid="listing-applicability-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="applicable">Applicable</SelectItem>
            <SelectItem value="not_applicable">Not applicable</SelectItem>
            <SelectItem value="auto_excluded">Auto-excluded</SelectItem>
            <SelectItem value="all">All items</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={expandAll} className="text-xs" data-testid="listing-expand-all">
          Expand all
        </Button>
        <Button variant="outline" size="sm" onClick={collapseAll} className="text-xs" data-testid="listing-collapse-all">
          Collapse all
        </Button>
      </div>

      <div className="space-y-3">
        {tiles.map((t) => (
          <PropertyCard
            key={t.location_id}
            tile={t}
            itemScope={itemScope}
            search={search}
            statusFilter={statusFilter}
            applicabilityFilter={applicabilityFilter}
            expanded={expandedByLoc.has(t.location_id)}
            onExpandChange={(next) => toggleExpanded(t.location_id, next)}
          />
        ))}
      </div>
    </div>
  );
}
